/**
 * One typed choke point for every product-funnel analytics event
 * (docs/APP-OVERHAUL-PLAN.md WS-M item 5, docs/ANALYTICS.md).
 *
 * Every call site imports `trackFunnel` plus one `FUNNEL_EVENTS` constant
 * instead of touching PostHog directly, so:
 *
 *   - event names live in one place a test can enumerate
 *     (`funnel.wiring.test.ts` greps every wired source file for
 *     `trackFunnel(FUNNEL_EVENTS.<X>` and fails if a real call site is ever
 *     deleted without deleting the constant too);
 *   - every event's payload is validated against a small closed shape
 *     (`matchesFunnelSpec`) before it can reach PostHog. Each spec only ever
 *     describes a fixed enum member or a non-negative count, so a free-text
 *     field — a trip title, a destination, an email — cannot travel through
 *     this path even by accident. An out-of-shape call (extra key, a string
 *     outside the enum, a negative/non-finite count) is dropped whole,
 *     never partially sent with the bad field stripped, so a misuse never
 *     silently ships in production.
 *
 * Loading and gating are shared with `PostHogProvider.tsx` through
 * `posthog-client.ts :: ensurePostHog`: same production+key gate, same lazy
 * `posthog-js` import so a dev or no-key environment never pulls the library
 * into the bundle at all — and because `ensurePostHog` initializes on first
 * demand, an event fired before the provider's mount effect (the earliest
 * `trip_created` / `itinerary_viewed` on a fresh page load) is captured
 * instead of silently dropped.
 */

import { ensurePostHog } from './posthog-client';

type EnumSpec<T extends string> = { readonly kind: 'enum'; readonly values: readonly T[] };
type CountSpec = { readonly kind: 'count' };
type FieldSpec = EnumSpec<string> | CountSpec;
type PropSpec = Record<string, FieldSpec>;

function enumField<T extends string>(...values: readonly T[]): EnumSpec<T> {
  return { kind: 'enum', values };
}

const countField: CountSpec = { kind: 'count' };

const TRIP_CREATED_SPEC = {
  door: enumField('blank', 'import', 'explore', 'wizard'),
} satisfies PropSpec;

const ITINERARY_REQUESTED_SPEC = {
  // Mirrors convex/schema.ts's real `tripBuildSourceKind` ('blank' | 'import'
  // | 'explore' — a wizard-created trip is still a 'blank'-sourced build
  // server-side) plus one client-only value: 'rebuild' fires from
  // `TripWorkspaceClient.tsx :: runBuild`, the one manual "start/regenerate
  // build" trigger shared by every trip regardless of how it was created.
  sourceKind: enumField('blank', 'import', 'explore', 'rebuild'),
} satisfies PropSpec;

const ITINERARY_VIEWED_SPEC = {
  status: enumField('ready', 'needs_review', 'building'),
  stops: countField,
  gaps: countField,
} satisfies PropSpec;

const ASK_ROAM_ASKED_SPEC = {
  kind: enumField('question', 'edit'),
} satisfies PropSpec;

// The three events below complete the original WS-M item 5 list
// (docs/APP-OVERHAUL-PLAN.md; docs/ANALYTICS.md's "Not wired, on purpose"
// section). All three waited for WS-I item 3's split of
// `TripWorkspaceClient.tsx` into `features/trip/*` because their real call
// sites lived inside that one file, alongside other work landing there at
// the same time.

const ASK_ROAM_EDIT_APPLIED_SPEC = {
  // 'inline': the server auto-applied a patch during a chat turn, with no
  // explicit user click to hang the event on. 'accepted_proposal': the
  // family clicked "Apply" on a proposal card -- whether that commits
  // through `acceptItineraryPatch` or the older `applySuggestion` path.
  kind: enumField('inline', 'accepted_proposal'),
} satisfies PropSpec;

const STOP_EDITED_SPEC = {
  kind: enumField('reorder', 'move', 'remove', 'time'),
} satisfies PropSpec;

const TRIP_SHARED_SPEC = {
  kind: enumField('invite', 'publish', 'link', 'export'),
} satisfies PropSpec;

const USER_SIGNED_UP_SPEC = {} satisfies PropSpec;

const ITINERARY_BUILT_SPEC = {
  outcome: enumField('ready', 'needs_review', 'failed'),
  sourceKind: enumField('blank', 'import', 'explore', 'rebuild'),
} satisfies PropSpec;

export const FUNNEL_EVENTS = {
  TRIP_CREATED: 'trip_created',
  ITINERARY_REQUESTED: 'itinerary_requested',
  ITINERARY_VIEWED: 'itinerary_viewed',
  ITINERARY_BUILT: 'itinerary_built',
  ASK_ROAM_ASKED: 'ask_roam_asked',
  ASK_ROAM_EDIT_APPLIED: 'ask_roam_edit_applied',
  STOP_EDITED: 'stop_edited',
  TRIP_SHARED: 'trip_shared',
  USER_SIGNED_UP: 'signed_up',
} as const;

export type FunnelEventName = (typeof FUNNEL_EVENTS)[keyof typeof FUNNEL_EVENTS];

type PropsOf<Spec extends PropSpec> = {
  [K in keyof Spec]: Spec[K] extends EnumSpec<infer T> ? T : number;
};

/**
 * Keyed by event name so `FUNNEL_EVENT_SPECS[event]` is a plain lookup.
 * Exported so `funnel.test.ts` validates the exact same specs `trackFunnel`
 * enforces at runtime, instead of a hand-duplicated copy that could drift.
 */
export const FUNNEL_EVENT_SPECS: Record<FunnelEventName, PropSpec> = {
  [FUNNEL_EVENTS.TRIP_CREATED]: TRIP_CREATED_SPEC,
  [FUNNEL_EVENTS.ITINERARY_REQUESTED]: ITINERARY_REQUESTED_SPEC,
  [FUNNEL_EVENTS.ITINERARY_VIEWED]: ITINERARY_VIEWED_SPEC,
  [FUNNEL_EVENTS.ASK_ROAM_ASKED]: ASK_ROAM_ASKED_SPEC,
  [FUNNEL_EVENTS.ASK_ROAM_EDIT_APPLIED]: ASK_ROAM_EDIT_APPLIED_SPEC,
  [FUNNEL_EVENTS.STOP_EDITED]: STOP_EDITED_SPEC,
  [FUNNEL_EVENTS.TRIP_SHARED]: TRIP_SHARED_SPEC,
  [FUNNEL_EVENTS.USER_SIGNED_UP]: USER_SIGNED_UP_SPEC,
  [FUNNEL_EVENTS.ITINERARY_BUILT]: ITINERARY_BUILT_SPEC,
};

export interface FunnelEventProps {
  [FUNNEL_EVENTS.TRIP_CREATED]: PropsOf<typeof TRIP_CREATED_SPEC>;
  [FUNNEL_EVENTS.ITINERARY_REQUESTED]: PropsOf<typeof ITINERARY_REQUESTED_SPEC>;
  [FUNNEL_EVENTS.ITINERARY_VIEWED]: PropsOf<typeof ITINERARY_VIEWED_SPEC>;
  [FUNNEL_EVENTS.ASK_ROAM_ASKED]: PropsOf<typeof ASK_ROAM_ASKED_SPEC>;
  [FUNNEL_EVENTS.ASK_ROAM_EDIT_APPLIED]: PropsOf<typeof ASK_ROAM_EDIT_APPLIED_SPEC>;
  [FUNNEL_EVENTS.STOP_EDITED]: PropsOf<typeof STOP_EDITED_SPEC>;
  [FUNNEL_EVENTS.TRIP_SHARED]: PropsOf<typeof TRIP_SHARED_SPEC>;
  [FUNNEL_EVENTS.USER_SIGNED_UP]: PropsOf<typeof USER_SIGNED_UP_SPEC>;
  [FUNNEL_EVENTS.ITINERARY_BUILT]: PropsOf<typeof ITINERARY_BUILT_SPEC>;
}

/**
 * Exported for `funnel.test.ts` — this is the "props schema" DO item 2 asks
 * for. Rejects anything that isn't an exact key-for-key match: a missing
 * field, an extra field (of any type, string or otherwise), an enum field
 * holding a value outside its listed members, or a count field holding
 * anything but a finite non-negative number.
 */
export function matchesFunnelSpec(spec: PropSpec, value: Record<string, unknown>): boolean {
  const specKeys = Object.keys(spec);
  const valueKeys = Object.keys(value);
  if (valueKeys.length !== specKeys.length) return false;
  for (const key of specKeys) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return false;
    const rule = spec[key]!;
    const fieldValue = value[key];
    if (rule.kind === 'enum') {
      if (typeof fieldValue !== 'string') return false;
      if (!(rule.values as readonly string[]).includes(fieldValue)) return false;
    } else {
      if (typeof fieldValue !== 'number' || !Number.isFinite(fieldValue) || fieldValue < 0) {
        return false;
      }
    }
  }
  return true;
}

/**
 * The one call site every product-funnel event goes through. Never throws:
 * an invalid payload is dropped (and logged to the console outside
 * production only — never in front of a real user), a disabled or
 * unconfigured environment (no key, non-production) resolves to a silent
 * no-op inside `ensurePostHog`, and a failed dynamic import is swallowed
 * the same way.
 */
export function trackFunnel<E extends FunnelEventName>(event: E, props: FunnelEventProps[E]): void {
  const spec = FUNNEL_EVENT_SPECS[event];
  if (!matchesFunnelSpec(spec, props as Record<string, unknown>)) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(`[funnel] "${event}" dropped: props do not match its schema`, props);
    }
    return;
  }
  ensurePostHog()
    .then((posthog) => {
      posthog?.capture(event, props);
    })
    .catch(() => undefined);
}
